import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toast } from '@buffer-mono/popcorn'

import type { Service } from '../../graphqlEnums'

import { useMonthlyCleanupResolve } from './hooks/useMonthlyCleanupResolve'

/** A channel offered for cleanup, with the fields the picker row renders. */
export type MonthlyCleanupChannel = {
  id: string
  name: string
  service: Service
  avatar: string
  /** Old-comment count, capped at `limit`. */
  count: number
  /** Backend cap applied to `count` (so the row can render "N+"). */
  limit: number
}

type UseMonthlyCleanupDialogParams = {
  channels: MonthlyCleanupChannel[]
  open: boolean
  /** Called once resolution finishes with at least one channel resolved. */
  onResolved?: () => void
}

type UseMonthlyCleanupDialogReturn = {
  /** Channel rows to render, snapshotted when the dialog opened. */
  channels: MonthlyCleanupChannel[]
  isSelected: (channelId: string) => boolean
  toggle: (channelId: string) => void
  resolving: boolean
  isResolved: (channelId: string) => boolean
  done: boolean
  resolvedTotal: number
  selectedCount: number
  selectedTotal: number
  resolve: () => Promise<void>
}

/**
 * State machine for the multi-channel cleanup dialog: channel selection →
 * sequential resolve (revealing a check per channel) → completion.
 *
 * The channel list is snapshotted when the dialog opens so the rows stay
 * stable through the whole sequence, even though the live counts that produced
 * them refetch and shrink to zero as comments get dismissed.
 */
export const useMonthlyCleanupDialog = ({
  channels,
  open,
  onResolved,
}: UseMonthlyCleanupDialogParams): UseMonthlyCleanupDialogReturn => {
  const [snapshot, setSnapshot] = useState<MonthlyCleanupChannel[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [resolving, setResolving] = useState(false)
  const [resolvedIds, setResolvedIds] = useState<string[]>([])
  const [done, setDone] = useState(false)
  const [resolvedTotal, setResolvedTotal] = useState(0)

  const { resolveChannels } = useMonthlyCleanupResolve()

  // Read the latest channels without making them an effect dependency, so the
  // reset below runs only on open — not when a live-count refetch swaps the
  // array mid-resolve.
  const channelsRef = useRef(channels)
  channelsRef.current = channels

  useEffect(() => {
    if (!open) return
    const current = channelsRef.current
    setSnapshot(current)
    setSelectedIds(current.map((channel) => channel.id))
    setResolving(false)
    setResolvedIds([])
    setDone(false)
    setResolvedTotal(0)
  }, [open])

  const isSelected = useCallback(
    (channelId: string): boolean => selectedIds.includes(channelId),
    [selectedIds],
  )

  const isResolved = useCallback(
    (channelId: string): boolean => resolvedIds.includes(channelId),
    [resolvedIds],
  )

  const toggle = useCallback(
    (channelId: string): void => {
      if (resolving) return
      setSelectedIds((prev) =>
        prev.includes(channelId)
          ? prev.filter((id) => id !== channelId)
          : [...prev, channelId],
      )
    },
    [resolving],
  )

  const selectedChannels = useMemo(
    () => snapshot.filter((channel) => selectedIds.includes(channel.id)),
    [snapshot, selectedIds],
  )
  const selectedCount = selectedChannels.length
  const selectedTotal = useMemo(
    () => selectedChannels.reduce((sum, channel) => sum + channel.count, 0),
    [selectedChannels],
  )

  const resolve = useCallback(async (): Promise<void> => {
    if (resolving || selectedChannels.length === 0) return

    setResolving(true)
    setResolvedIds([])

    const { resolvedChannelIds, hasFailure } = await resolveChannels(
      selectedChannels.map((channel) => channel.id),
      (channelId) => setResolvedIds((prev) => [...prev, channelId]),
    )

    const total = snapshot
      .filter((channel) => resolvedChannelIds.includes(channel.id))
      .reduce((sum, channel) => sum + channel.count, 0)

    setResolvedTotal(total)
    setResolving(false)
    // Only advance to the success screen if something actually resolved; a
    // total failure leaves the picker up so the user can retry.
    setDone(resolvedChannelIds.length > 0)

    if (hasFailure) {
      toast.error('Some comments could not be resolved. Please try again.')
    }
    if (resolvedChannelIds.length > 0) {
      onResolved?.()
    }
  }, [resolving, selectedChannels, resolveChannels, snapshot, onResolved])

  return {
    channels: snapshot,
    isSelected,
    toggle,
    resolving,
    isResolved,
    done,
    resolvedTotal,
    selectedCount,
    selectedTotal,
    resolve,
  }
}
